import { arcade, bindPointer } from './arcade.js';
"use strict";
    /* =====================================================================
       GALAGA — authentic 1981 Namco-style rebuild
       Pure vanilla JS + Canvas 2D. Portrait arcade play area.
       Internal backing resolution: 224 x 288 logical pixels, drawn at 2x
       into a 448 x 576 canvas so everything stays crisp & integer-aligned.
       ===================================================================== */

    // ---- Constants -----------------------------------------------------
    const VW = 224;            // logical (virtual) game width
    const VH = 288;            // logical game height
    const SCALE = 2;           // backing-store multiplier (canvas is 448x576)
    const PLAYER_Y = VH - 26;  // fixed player vertical position
    const PLAYER_SPEED = 1.3;  // logical px / frame
    const PLAYER_BULLET_SPEED = 4.2;
    const ENEMY_BULLET_SPEED = 1.4;
    const SHOT_COOLDOWN = 220; // ms between shots
    const RESPAWN_INVULN = 2200; // ms of blink invulnerability after death

    const reduceMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- Canvas --------------------------------------------------------
    const canvas = document.getElementById('gameCanvas');
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    // ---- Audio (chiptune via Web Audio, all guarded) -------------------
    let audioCtx = null;
    try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
        audioCtx = null;
    }
    function resumeAudio() {
        if (audioCtx && audioCtx.state === 'suspended') {
            audioCtx.resume().catch(function () {});
        }
    }
    // Generic short tone.
    function tone(freq, dur, type, vol, freqEnd) {
        if (!audioCtx || !arcade.soundEnabled) return;
        try {
            const t0 = audioCtx.currentTime;
            const osc = audioCtx.createOscillator();
            const g = audioCtx.createGain();
            osc.type = type || 'square';
            osc.frequency.setValueAtTime(freq, t0);
            if (typeof freqEnd === 'number') {
                osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + dur);
            }
            g.gain.setValueAtTime(vol || 0.18, t0);
            g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
            osc.connect(g);
            g.connect(audioCtx.destination);
            osc.start(t0);
            osc.stop(t0 + dur + 0.02);
        } catch (e) { /* ignore */ }
    }
    // White-noise burst (explosions).
    function noiseBurst(dur, vol) {
        if (!audioCtx || !arcade.soundEnabled) return;
        try {
            const t0 = audioCtx.currentTime;
            const len = Math.floor(audioCtx.sampleRate * dur);
            const buf = audioCtx.createBuffer(1, len, audioCtx.sampleRate);
            const data = buf.getChannelData(0);
            for (let i = 0; i < len; i++) {
                data[i] = (Math.random() * 2 - 1) * (1 - i / len);
            }
            const src = audioCtx.createBufferSource();
            src.buffer = buf;
            const g = audioCtx.createGain();
            g.gain.setValueAtTime(vol || 0.25, t0);
            g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
            const filt = audioCtx.createBiquadFilter();
            filt.type = 'lowpass';
            filt.frequency.value = 1600;
            src.connect(filt);
            filt.connect(g);
            g.connect(audioCtx.destination);
            src.start(t0);
            src.stop(t0 + dur);
        } catch (e) { /* ignore */ }
    }
    const sfx = {
        shoot: function () { tone(880, 0.08, 'square', 0.12, 220); },
        explode: function () { noiseBurst(0.3, 0.3); },
        hit: function () { tone(330, 0.07, 'triangle', 0.15, 180); },
        dive: function () { tone(200, 0.25, 'sawtooth', 0.1, 700); },
        capture: function () { // descending eerie tractor tone
            tone(700, 0.5, 'sine', 0.18, 120);
            tone(350, 0.5, 'sine', 0.12, 80);
        },
        rescue: function () { // rising fanfare
            tone(523, 0.1, 'square', 0.15);
            setTimeout(function () { tone(659, 0.1, 'square', 0.15); }, 90);
            setTimeout(function () { tone(784, 0.1, 'square', 0.15); }, 180);
            setTimeout(function () { tone(1046, 0.2, 'square', 0.16); }, 270);
        },
        stageStart: function () {
            tone(523, 0.12, 'square', 0.14);
            setTimeout(function () { tone(784, 0.12, 'square', 0.14); }, 120);
            setTimeout(function () { tone(1046, 0.16, 'square', 0.14); }, 240);
        },
        coin: function () { tone(988, 0.06, 'square', 0.13); setTimeout(function(){tone(1318,0.1,'square',0.13);}, 60); }
    };

    // ---- Pixel font ----------------------------------------------------
    // Hand-rolled 5x5-ish bitmap font (each glyph is 5 rows of 4 cols).
    // 1 = lit pixel. Drawn with fillRect so it stays chunky & crisp.
    const FONT = {
        '0': ["1111","1001","1001","1001","1111"],
        '1': ["0010","0110","0010","0010","0111"],
        '2': ["1111","0001","1111","1000","1111"],
        '3': ["1111","0001","0111","0001","1111"],
        '4': ["1001","1001","1111","0001","0001"],
        '5': ["1111","1000","1111","0001","1111"],
        '6': ["1111","1000","1111","1001","1111"],
        '7': ["1111","0001","0010","0100","0100"],
        '8': ["1111","1001","1111","1001","1111"],
        '9': ["1111","1001","1111","0001","1111"],
        'A': ["0110","1001","1111","1001","1001"],
        'B': ["1110","1001","1110","1001","1110"],
        'C': ["0111","1000","1000","1000","0111"],
        'D': ["1110","1001","1001","1001","1110"],
        'E': ["1111","1000","1110","1000","1111"],
        'F': ["1111","1000","1110","1000","1000"],
        'G': ["0111","1000","1011","1001","0111"],
        'H': ["1001","1001","1111","1001","1001"],
        'I': ["111","010","010","010","111"],
        'J': ["0011","0001","0001","1001","0110"],
        'K': ["1001","1010","1100","1010","1001"],
        'L': ["1000","1000","1000","1000","1111"],
        'M': ["10001","11011","10101","10001","10001"],
        'N': ["1001","1101","1011","1001","1001"],
        'O': ["0110","1001","1001","1001","0110"],
        'P': ["1110","1001","1110","1000","1000"],
        'Q': ["0110","1001","1001","1011","0111"],
        'R': ["1110","1001","1110","1010","1001"],
        'S': ["0111","1000","0110","0001","1110"],
        'T': ["11111","00100","00100","00100","00100"],
        'U': ["1001","1001","1001","1001","0110"],
        'V': ["1001","1001","1001","0110","0110"],
        'W': ["10001","10001","10101","11011","10001"],
        'X': ["1001","1001","0110","1001","1001"],
        'Y': ["10001","01010","00100","00100","00100"],
        'Z': ["1111","0010","0100","1000","1111"],
        '-': ["0000","0000","1111","0000","0000"],
        '!': ["010","010","010","000","010"],
        '.': ["00","00","00","00","11"],
        ':': ["00","11","00","11","00"],
        '/': ["0001","0010","0100","1000","0000"],
        ' ': ["000","000","000","000","000"]
    };
    // Measure rendered width of a string for a given pixel size.
    function textWidth(str, px) {
        let w = 0;
        for (let i = 0; i < str.length; i++) {
            const g = FONT[str[i].toUpperCase()] || FONT[' '];
            w += (g[0].length + 1) * px; // +1 col spacing
        }
        return w - px;
    }
    // Draw arcade text at logical coords (caller already applied SCALE via ctx transform).
    function drawText(str, x, y, px, color) {
        ctx.fillStyle = color || '#fff';
        let cx = x;
        for (let i = 0; i < str.length; i++) {
            const g = FONT[str[i].toUpperCase()] || FONT[' '];
            for (let r = 0; r < g.length; r++) {
                const row = g[r];
                for (let c = 0; c < row.length; c++) {
                    if (row[c] === '1') {
                        ctx.fillRect(cx + c * px, y + r * px, px, px);
                    }
                }
            }
            cx += (g[0].length + 1) * px;
        }
    }
    function drawTextCentered(str, y, px, color) {
        drawText(str, Math.round((VW - textWidth(str, px)) / 2), y, px, color);
    }

    // ---- Sprite system -------------------------------------------------
    // Each sprite is an array of equal-length strings; chars index a palette
    // object ('.' = transparent). drawSprite fills integer-aligned rects.
    function drawSprite(grid, palette, x, y, scale) {
        scale = scale || 1;
        x = Math.round(x);
        y = Math.round(y);
        for (let r = 0; r < grid.length; r++) {
            const row = grid[r];
            for (let c = 0; c < row.length; c++) {
                const ch = row[c];
                if (ch === '.' ) continue;
                const col = palette[ch];
                if (!col) continue;
                ctx.fillStyle = col;
                ctx.fillRect(x + c * scale, y + r * scale, scale, scale);
            }
        }
    }

    // -- Palettes --
    const PAL = {
        W: '#ffffff', R: '#ff2020', B: '#3858ff', C: '#28e0f0', Y: '#ffe000',
        G: '#28d028', P: '#c040ff', K: '#000000', O: '#ff8000', g: '#108010'
    };

    // -- Player fighter (13x16): white body, red wing tips, cyan core --
    const SPR_PLAYER = [
        ".......W.......",
        ".......W.......",
        "......WWW......",
        "......WCW......",
        "......WCW......",
        ".....WWCWW.....",
        ".....WWCWW.....",
        "....WWWCWWW....",
        "...RWWWCWWWR...",
        "..RRWWWCWWWRR..",
        ".RRRWWWCWWWRRR.",
        ".RRRWWWWWWWRRR.",
        ".R.WWWWWWWWW.R.",
        "...WW.WWW.WW...",
        "....W..W..W....",
        ".......W......."
    ];

    // -- Bee / Zako (blue & yellow), 2 anim frames (13x12) --
    const SPR_BEE_A = [
        ".....Y...Y.....",
        "......Y.Y......",
        "..B...YYY...B..",
        "..BB.YBYBY.BB..",
        ".BBBBYYYYYBBBB.",
        "BBYBBBYYYBBBYBB",
        "BBYBBBBYBBBBYBB",
        ".BB.BYYYYYB.BB.",
        "....BBYYYBB....",
        "...BB.....BB...",
        "..B.........B..",
        ".B...........B."
    ];
    const SPR_BEE_B = [
        ".B...Y...Y...B.",
        "..B...Y.Y...B..",
        "..BB..YYY..BB..",
        "...B.YBYBY.B...",
        ".BBBBYYYYYBBBB.",
        "BBYBBBYYYBBBYBB",
        "BBYBBBBYBBBBYBB",
        "..B.BYYYYYB.B..",
        "....BBYYYBB....",
        ".....B...B.....",
        "....B.....B....",
        "...B.......B..."
    ];

    // -- Butterfly / Goei (red & white with blue), 2 frames (13x13) --
    const SPR_BUT_A = [
        "......R......",
        ".....RRR.....",
        "R...RWRWR...R",
        "RR.RRWRWRR.RR",
        "RRRBRWRWRBRRR",
        ".RRBBRRRBBRR.",
        "..RBWWRWWBR..",
        "...RWWRWWR...",
        "..RRRWRWRRR..",
        ".RR..RRR..RR.",
        "RR...R.R...RR",
        "R...RR.RR...R",
        "...RR...RR..."
    ];
    const SPR_BUT_B = [
        "R.....R.....R",
        "RR...RRR...RR",
        "RRR.RWRWR.RRR",
        ".RRRRWRWRRRR.",
        "..RBRWRWRBR..",
        "..RBBRRRBBR..",
        "...BWWRWWB...",
        "...RWWRWWR...",
        "....RWRWR....",
        "...RRRRRRR...",
        "..RR..R..RR..",
        ".R...RRR...R.",
        "R...RR.RR...R"
    ];

    // -- Boss Galaga (green dome + blue body), 2 frames (15x14) --
    const SPR_BOSS_A = [
        ".....GGGGG.....",
        "...GGGGGGGGG...",
        "..GGCGGGGGCGG..",
        "..GGGGGGGGGGG..",
        ".BBGGGGGGGGGBB.",
        "BBBBGGGGGGGBBBB",
        "BBYBBGGGGGBBYBB",
        "BBYBBBBBBBBBYBB",
        ".BBBBYYYYYBBBB.",
        "..BB.YYYYY.BB..",
        "...B.YY.YY.B...",
        "..BB.Y...Y.BB..",
        ".BB...........BB",
        "B...............B"
    ];
    const SPR_BOSS_B = [
        ".....GGGGG.....",
        "...GGGGGGGGG...",
        "..GGCGGGGGCGG..",
        "..GGGGGGGGGGG..",
        "BBBGGGGGGGGGBBB",
        "BBBBGGGGGGGBBBB",
        "BBYBBGGGGGBBYBB",
        ".BYBBBBBBBBBYB.",
        "..BBBYYYYYBBB..",
        "...B.YYYYY.B...",
        "..BB.YY.YY.BB..",
        ".BB..Y...Y..BB.",
        "B....Y...Y....B",
        "......B.B......"
    ];
    // Boss damaged (hit once) — blue/purple flashing version (15x14)
    const SPR_BOSS_HURT = [
        ".....PPPPP.....",
        "...PPPPPPPPP...",
        "..PPCPPPPPCPP..",
        "..PPPPPPPPPPP..",
        ".BBPPPPPPPPPBB.",
        "BBBBPPPPPPPBBBB",
        "BBWBBPPPPPBBWBB",
        "BBWBBBBBBBBBWBB",
        ".BBBBWWWWWBBBB.",
        "..BB.WWWWW.BB..",
        "...B.WW.WW.B...",
        "..BB.W...W.BB..",
        ".BB...........BB",
        "B...............B"
    ];

    // -- Captured fighter (player ship tinted, shown above a boss) --
    // Reuse player sprite; tint handled by drawing a slightly different pal.
    const PAL_CAPTURED = {
        W: '#80c0ff', R: '#ff2020', C: '#28e0f0', K: '#000000'
    };

    // -- Explosion frames (expanding) 16x16, classic Galaga burst --
    const EXP = [
        [
            "................",
            "................",
            "................",
            "......O.O.......",
            ".......OO.......",
            "......OOOO......",
            ".......OO.......",
            "......O.O.......",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................"
        ],
        [
            "................",
            "................",
            "....O.....O.....",
            ".....O.Y.O......",
            "......YOY.......",
            "...O.YOOOY.O....",
            "......YOY.......",
            ".....O.Y.O......",
            "....O.....O.....",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................"
        ],
        [
            "................",
            "...O..O.O..O....",
            "..O..YO.OY..O...",
            "....YO.R.OY.....",
            "...O.RO.OR.O....",
            "..O.O.OOO.O.O...",
            "...O.RO.OR.O....",
            "....YO.R.OY.....",
            "..O..YO.OY..O...",
            "...O..O.O..O....",
            "................",
            "................",
            "................",
            "................",
            "................",
            "................"
        ],
        [
            ".O...O...O...O..",
            "..O.O.Y.Y.O.O...",
            "O..O.R...R.O..O.",
            "..O.R..Y..R.O...",
            ".Y.R..OOO..R.Y..",
            "O...O.OYO.O...O.",
            ".Y.R..OOO..R.Y..",
            "..O.R..Y..R.O...",
            "O..O.R...R.O..O.",
            "..O.O.Y.Y.O.O...",
            ".O...O...O...O..",
            "................",
            "................",
            "................",
            "................",
            "................"
        ],
        [
            "O....O...O....O.",
            "..O..............",
            "....O..Y.Y..O...",
            ".O....R...R...O.",
            "...R...O.O...R..",
            ".Y...O.....O..Y.",
            "..O...........O.",
            "O.....O...O....O",
            "..O...........O.",
            ".Y...O.....O..Y.",
            "...R...O.O...R..",
            ".O....R...R...O.",
            "....O..Y.Y..O...",
            "..O.............",
            "O....O...O....O.",
            "................"
        ]
    ];
    const PAL_EXP = { O: '#ff8000', Y: '#ffe000', R: '#ff2020' };

    // ---- Enemy formation layout ---------------------------------------
    // Classic Galaga grid: 10 columns wide, rows of boss/butterfly/bee.
    const GRID_COLS = 10;
    const CELL_W = 16;     // horizontal spacing between formation slots
    const CELL_H = 17;     // vertical spacing
    const FORM_TOP = 36;   // y of the top formation row
    const FORM_LEFT = (VW - (GRID_COLS - 1) * CELL_W) / 2; // centered

    // Enemy kind metadata.
    const KIND = {
        bee:       { w: 13, h: 12, frames: [SPR_BEE_A, SPR_BEE_B], pal: PAL, scoreForm: 50,  scoreDive: 100, hp: 1, fire: 0.0016 },
        butterfly: { w: 13, h: 13, frames: [SPR_BUT_A, SPR_BUT_B], pal: PAL, scoreForm: 80,  scoreDive: 160, hp: 1, fire: 0.0020 },
        boss:      { w: 15, h: 14, frames: [SPR_BOSS_A, SPR_BOSS_B], pal: PAL, scoreForm: 150, scoreDive: 400, hp: 2, fire: 0.0024 }
    };

    // ---- Game state ----------------------------------------------------
    let gameState = 'start'; // start, fly-in, playing, stage-clear, dying, gameover
    let score = 0;
    let shotsFired = 0;
    let shotsHit = 0;
    let nextExtraLife = 20000;
    let simulationTime = 0;
    let highScore = 0;
    try { highScore = parseInt(localStorage.getItem('galagaHighScore') || '0', 10) || 0; } catch (e) {}
    let lives = 3;
    let stage = 1;
    let isChallenge = false;

    const keys = {};
    let lastShotTime = 0;
    let shakeTime = 0;        // screen-shake timer (ms)
    let stageBannerTimer = 0; // ms remaining to show STAGE banner
    let perfectTimer = 0;     // ms remaining to show PERFECT
    let blinkPhase = 0;       // global blink counter for flashing things

    // Player object (supports dual fighter).
    const player = {
        x: VW / 2,
        alive: true,
        dual: false,           // dual fighter active?
        invulnUntil: 0,        // ms timestamp of invuln end
        respawning: false,
        captured: false,       // currently being tractor-captured (anim)
        captureAnim: 0
    };

    let bullets = [];          // player bullets
    let enemyBullets = [];
    let enemies = [];          // all live enemies (formation + diving + flying in)
    let particles = [];        // explosion sprites + sparks
    let popups = [];           // floating score text
    let stars = [];            // starfield
    let flyInQueue = [];       // groups still to spawn during fly-in
    let flyInTimer = 0;
    let diveTimer = 0;         // ms accumulator for scheduling dives
    let captureCooldown = 0;   // ms before another capture attempt may start
    let captureActive = false; // a boss is currently running a capture dive

    // The boss that currently holds a captured fighter (for rescue logic).
    let capturedHolder = null;

    // Challenging-stage hit tracking: the PERFECT bonus is only awarded if the
    // player destroys every enemy that flies through (8 waves x 5 = 40).
    const CHALLENGE_TOTAL = 40;
    let challengeKills = 0;

    // ---- Starfield -----------------------------------------------------
    function initStars() {
        stars = [];
        const colors = ['#ffffff', '#ffffff', '#ffffff', '#88aaff', '#ff8888', '#ffff88'];
        for (let i = 0; i < 80; i++) {
            stars.push({
                x: Math.floor(Math.random() * VW),
                y: Math.floor(Math.random() * VH),
                speed: 0.2 + Math.random() * 0.6,
                color: colors[Math.floor(Math.random() * colors.length)],
                twinkle: Math.random() * Math.PI * 2,
                twSpeed: 0.05 + Math.random() * 0.1
            });
        }
    }
    function updateStars(dt) {
        for (let i = 0; i < stars.length; i++) {
            const s = stars[i];
            s.y += s.speed * dt;
            if (s.y >= VH) { s.y = 0; s.x = Math.floor(Math.random() * VW); }
            s.twinkle += s.twSpeed * dt;
        }
    }
    function drawStars() {
        for (let i = 0; i < stars.length; i++) {
            const s = stars[i];
            // Twinkle: skip drawing when in the "off" part of the sine wave.
            if (Math.sin(s.twinkle) > -0.3) {
                ctx.fillStyle = s.color;
                ctx.fillRect(Math.floor(s.x), Math.floor(s.y), 1, 1);
            }
        }
    }

    // ---- Enemy factory -------------------------------------------------
    function makeEnemy(kind, col, row, slotX, slotY) {
        const meta = KIND[kind];
        return {
            kind: kind,
            col: col, row: row,
            homeX: slotX, homeY: slotY,   // formation slot (relative anchor applied later)
            x: slotX, y: slotY,
            w: meta.w, h: meta.h,
            hp: meta.hp,
            state: 'flyin',               // flyin | formation | dive | return
            // fly-in path
            path: null, pathT: 0, pathDur: 0,
            // dive
            diveT: 0, divePath: null, diveDur: 0,
            angle: 0,
            // boss capture
            isCapturing: false,
            beam: 0,                      // beam open amount 0..1
            holdsCaptured: false,         // boss currently holds player's ship
            // escort link (for boss V dives)
            escortLeader: null, escortOffset: 0,
            // animation
            flap: Math.random() * Math.PI * 2,
            hurtFlash: 0,
            fireChance: meta.fire
        };
    }

    // Assign a kind to a formation row (Galaga layout).
    function kindForRow(row) {
        if (row === 0) return 'boss';        // top row: flagships (but only middle cols get bosses)
        if (row <= 2) return 'butterfly';    // next 2 rows
        return 'bee';                         // bottom rows
    }

    // Build the formation roster + a staggered fly-in queue.
    function buildStage() {
        enemies = [];
        flyInQueue = [];
        capturedHolder = null;
        captureActive = false;
        captureCooldown = 4000 + Math.random() * 3000;

        if (isChallenge) {
            buildChallengeStage();
            return;
        }

        // Standard formation: 5 rows. Row 0 has 4 bosses (cols 3..6).
        const rows = 5;
        const roster = [];
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < GRID_COLS; col++) {
                let kind = kindForRow(row);
                if (kind === 'boss') {
                    // Only the central 4 columns are bosses; sides skip row 0.
                    if (col < 3 || col > 6) continue;
                }
                const slotX = FORM_LEFT + col * CELL_W;
                const slotY = FORM_TOP + row * CELL_H;
                roster.push(makeEnemy(kind, col, row, slotX, slotY));
            }
        }

        // Group the roster into fly-in waves of ~6 that share an entry path.
        // Alternate entry sides for visual variety.
        let group = [];
        let groupIndex = 0;
        for (let i = 0; i < roster.length; i++) {
            group.push(roster[i]);
            if (group.length >= 6 || i === roster.length - 1) {
                flyInQueue.push({ members: group, side: groupIndex % 2, delay: groupIndex * 650 });
                group = [];
                groupIndex++;
            }
        }
        flyInTimer = 0;
    }

    // Challenging stage: enemies fly through in patterns, do not shoot.
    function buildChallengeStage() {
        // 8 waves of 5 enemies sweeping across; they never reach formation.
        challengeKills = 0;
        const kinds = ['bee', 'butterfly', 'boss'];
        let groupIndex = 0;
        for (let g = 0; g < 8; g++) {
            const members = [];
            const kind = kinds[g % kinds.length];
            for (let k = 0; k < 5; k++) {
                const e = makeEnemy(kind, k, 0, VW / 2, -20);
                e.challenge = true;       // never docks, never shoots
                e.hp = KIND[kind].hp;
                members.push(e);
            }
            flyInQueue.push({ members: members, side: g % 2, delay: g * 900, challenge: true });
            groupIndex++;
        }
        flyInTimer = 0;
    }

    // ---- Bezier helpers for curved paths -------------------------------
    function bezier(p0, p1, p2, p3, t) {
        const u = 1 - t;
        const tt = t * t, uu = u * u;
        const uuu = uu * u, ttt = tt * t;
        return uuu * p0 + 3 * uu * t * p1 + 3 * u * tt * p2 + ttt * p3;
    }

    // Create a looping fly-in path from off-screen to a formation slot.
    function makeFlyInPath(side, slotX, slotY, idx) {
        // Entry from a side, loop, then approach the slot.
        const startX = side === 0 ? -20 : VW + 20;
        const startY = 40 + idx * 6;
        // A swirl control set producing a loop.
        const cx = side === 0 ? VW * 0.30 : VW * 0.70;
        const p1x = side === 0 ? VW * 0.55 : VW * 0.45;
        const p1y = 30;
        const p2x = cx;
        const p2y = 120;
        return {
            p0x: startX, p0y: startY,
            p1x: p1x, p1y: p1y,
            p2x: p2x, p2y: p2y,
            p3x: slotX, p3y: slotY
        };
    }

    // Challenge-stage sweep path: in one side, big arc, out the other/bottom.
    function makeChallengePath(side, idx) {
        const startX = side === 0 ? -20 : VW + 20;
        const startY = 20 + idx * 10;
        const endX = side === 0 ? VW + 20 : -20;
        const endY = 200 - idx * 8;
        return {
            p0x: startX, p0y: startY,
            p1x: VW * 0.25, p1y: 150,
            p2x: VW * 0.75, p2y: 60 + idx * 12,
            p3x: endX, p3y: endY
        };
    }

    // ---- Stage / wave control -----------------------------------------
    function beginStage() {
        isChallenge = (stage % 3 === 0);
        buildStage();
        gameState = 'flyin';
        stageBannerTimer = isChallenge ? 1800 : 1400;
        perfectTimer = 0;
        diveTimer = 0;
        sfx.stageStart();
        updateUI();
    }

    function startFlyInGroup(grp) {
        for (let i = 0; i < grp.members.length; i++) {
            const e = grp.members[i];
            e.state = 'flyin';
            if (grp.challenge) {
                e.path = makeChallengePath(grp.side, i);
            } else {
                e.path = makeFlyInPath(grp.side, e.homeX, e.homeY, i);
            }
            e.pathT = -i * 0.12;          // small stagger inside the group
            e.pathDur = grp.challenge ? 2.0 : 1.6;
            e.x = e.path.p0x;
            e.y = e.path.p0y;
            enemies.push(e);
        }
        sfx.dive();
    }

    // ---- Formation breathing -------------------------------------------
    let formationPhase = 0;
    function formationOffsetX() {
        // Sway side to side.
        return Math.sin(formationPhase) * 8;
    }
    function formationScale() {
        // Slight expand/contract breathing.
        return 1 + Math.sin(formationPhase * 0.7) * 0.04;
    }
    function slotPosition(e) {
        const sx = formationScale();
        const cxCenter = FORM_LEFT + ((GRID_COLS - 1) * CELL_W) / 2;
        const x = cxCenter + (e.homeX - cxCenter) * sx + formationOffsetX();
        const y = e.homeY;
        return { x: x, y: y };
    }

    // ---- Diving logic --------------------------------------------------
    function pickDivers() {
        // Choose formation enemies to peel off.
        const candidates = enemies.filter(function (e) {
            return e.state === 'formation' && !e.isCapturing;
        });
        if (candidates.length === 0) return;

        // Maybe trigger a boss capture dive instead of a normal dive.
        if (!captureActive && captureCooldown <= 0 && !player.dual && !player.captured && capturedHolder === null) {
            const bosses = candidates.filter(function (e) { return e.kind === 'boss'; });
            if (bosses.length > 0 && Math.random() < 0.5) {
                startCaptureDive(bosses[Math.floor(Math.random() * bosses.length)]);
                return;
            }
        }

        // Normal dive: 1-3 enemies. Bosses lead escorts in a V.
        const maxDivers = Math.min(1 + Math.floor(stage / 2), 3);
        const count = 1 + Math.floor(Math.random() * maxDivers);
        for (let n = 0; n < count; n++) {
            const e = candidates[Math.floor(Math.random() * candidates.length)];
            if (!e || e.state !== 'formation') continue;
            if (e.kind === 'boss') {
                startDive(e);
                // Give the boss up to 2 escorts from nearby butterflies/bees.
                const escorts = candidates.filter(function (c) {
                    return c.state === 'formation' && c !== e && c.kind !== 'boss';
                }).slice(0, 2);
                for (let j = 0; j < escorts.length; j++) {
                    startDive(escorts[j], e, (j === 0 ? -10 : 10));
                }
            } else {
                startDive(e);
            }
        }
    }

    function startDive(e, leader, offset) {
        e.state = 'dive';
        e.diveT = 0;
        e.diveDur = 2.4;
        e.escortLeader = leader || null;
        e.escortOffset = offset || 0;
        const pos = slotPosition(e);
        // Swooping dive path toward the player's side then off bottom.
        const targetX = player.x + (Math.random() * 60 - 30);
        const dir = pos.x < VW / 2 ? 1 : -1;
        e.divePath = {
            p0x: pos.x, p0y: pos.y,
            p1x: pos.x + dir * 30, p1y: pos.y + 40,
            p2x: targetX - dir * 40, p2y: VH * 0.6,
            p3x: targetX, p3y: VH + 20
        };
        sfx.dive();
    }

    function startCaptureDive(boss) {
        captureActive = true;
        boss.isCapturing = true;
        boss.state = 'capture';
        boss.captureT = 0;
        boss.beam = 0;
        boss.capturePhase = 'descend'; // descend -> beam -> retreat
        const pos = slotPosition(boss);
        boss.captureFromX = pos.x;
        boss.captureFromY = pos.y;
        // Descends to about 45% down, roughly above the player.
        boss.captureTargetX = Math.max(30, Math.min(VW - 30, player.x));
        boss.captureTargetY = VH * 0.42;
        sfx.dive();
    }

    // ---- Update: fly-in -------------------------------------------------
    function updateFlyIn(dt, dtMs) {
        flyInTimer += dtMs;
        // Release queued groups on schedule.
        for (let i = flyInQueue.length - 1; i >= 0; i--) {
            if (flyInTimer >= flyInQueue[i].delay) {
                startFlyInGroup(flyInQueue[i]);
                flyInQueue.splice(i, 1);
            }
        }

        // Advance fly-in motion (handled in updateEnemies). When all enemies
        // have docked (or, for challenge, left) and the queue is empty, the
        // stage proper begins.
        const allDone = flyInQueue.length === 0 && enemies.every(function (e) {
            return e.state === 'formation' || (e.challenge && false);
        });

        if (isChallenge) {
            // Challenge stages stay in the 'flyin' state for their whole
            // duration. The loop's 'flyin' branch already runs the player,
            // bullets and collisions, and staying here keeps updateFlyIn
            // draining flyInQueue each frame so all 8 waves are released on
            // schedule. The stage ends only once every wave has been released
            // and no enemies remain (shot down or swept off-screen).
            if (flyInQueue.length === 0 && enemies.length === 0) {
                finishChallenge();
            }
        } else if (allDone && enemies.length > 0) {
            gameState = 'playing';
            diveTimer = 0;
        }
    }

    function finishChallenge() {
        // Award the PERFECT bonus only if every challenge enemy was destroyed
        // (the authentic rule); otherwise show how many were hit.
        if (challengeKills >= CHALLENGE_TOTAL) {
            perfectTimer = 1800;
            score += 10000;
            sfx.rescue();
            spawnPopup(VW / 2 - 20, VH / 2, 'PERFECT', '#ffe000');
        } else {
            spawnPopup(VW / 2 - 32, VH / 2,
                challengeKills + ' / ' + CHALLENGE_TOTAL + ' HITS', '#ffffff');
        }
        updateUI();
        gameState = 'stage-clear';
        stageClearTimer = 1800;
    }

    // ---- Update: enemies ------------------------------------------------
    let stageClearTimer = 0;
    function updateEnemies(dt, dtMs) {
        formationPhase += 0.02 * dt;

        for (let i = enemies.length - 1; i >= 0; i--) {
            const e = enemies[i];
            e.flap += 0.08 * dt;
            if (e.hurtFlash > 0) e.hurtFlash -= dtMs;

            if (e.state === 'flyin') {
                e.pathT += (dt / 60) / e.pathDur;
                const t = Math.max(0, Math.min(1, e.pathT));
                const p = e.path;
                const nx = bezier(p.p0x, p.p1x, p.p2x, p.p3x, t);
                const ny = bezier(p.p0y, p.p1y, p.p2y, p.p3y, t);
                // face direction of travel
                e.angle = Math.atan2(ny - e.y, nx - e.x);
                e.x = nx; e.y = ny;
                if (e.pathT >= 1) {
                    if (e.challenge) {
                        // Sweep complete: leave the screen (remove).
                        enemies.splice(i, 1);
                        continue;
                    }
                    e.state = 'formation';
                    e.angle = 0;
                }
            } else if (e.state === 'formation') {
                const pos = slotPosition(e);
                e.x = pos.x; e.y = pos.y;
                e.angle = 0;
                // Formation fire (rare). Challenge enemies never shoot.
                if (gameState === 'playing' && !e.challenge &&
                    Math.random() < e.fireChance * dt * (0.6 + stage * 0.08)) {
                    fireEnemyBullet(e);
                }
            } else if (e.state === 'dive') {
                e.diveT += (dt / 60) / e.diveDur;
                const t = Math.min(1, e.diveT);
                const p = e.divePath;
                let nx = bezier(p.p0x, p.p1x, p.p2x, p.p3x, t);
                let ny = bezier(p.p0y, p.p1y, p.p2y, p.p3y, t);
                // Escorts trail their leader with a sideways offset (V shape).
                if (e.escortLeader && e.escortLeader.state === 'dive') {
                    nx += e.escortOffset;
                }
                e.angle = Math.atan2(ny - e.y, nx - e.x) - Math.PI / 2;
                e.x = nx; e.y = ny;
                // Fire while diving.
                if (Math.random() < 0.012 * dt) fireEnemyBullet(e);
                if (t >= 1) {
                    // Wrap to top and re-enter the formation slot.
                    e.state = 'return';
                    e.x = e.homeX + formationOffsetX();
                    e.y = -e.h;
                    e.returnT = 0;
                }
            } else if (e.state === 'return') {
                // Glide back down into the formation slot.
                const pos = slotPosition(e);
                const dx = pos.x - e.x;
                const dy = pos.y - e.y;
                e.x += dx * 0.08 * dt;
                e.y += dy * 0.08 * dt;
                e.angle = 0;
                if (Math.abs(dx) < 1.5 && Math.abs(dy) < 1.5) {
                    e.state = 'formation';
                }
            } else if (e.state === 'capture') {
                updateCaptureBoss(e, dt, dtMs);
            }
        }
    }

    // Boss capture-dive state machine.
    function updateCaptureBoss(boss, dt, dtMs) {
        boss.captureT += dtMs;
        if (boss.capturePhase === 'descend') {
            // Ease toward the target capture position.
            boss.x += (boss.captureTargetX - boss.x) * 0.05 * dt;
            boss.y += (boss.captureTargetY - boss.y) * 0.05 * dt;
            if (Math.abs(boss.y - boss.captureTargetY) < 3) {
                boss.capturePhase = 'beam';
                boss.captureT = 0;
                sfx.capture();
            }
        } else if (boss.capturePhase === 'beam') {
            // Open the beam over ~0.6s, hold ~1.4s, then close.
            const open = Math.min(1, boss.captureT / 600);
            boss.beam = open;
            // Check capture: player within the cone and not invulnerable.
            if (!player.captured && player.alive && tnow() > player.invulnUntil) {
                if (isPlayerInBeam(boss)) {
                    triggerCapture(boss);
                }
            }
            if (boss.captureT > 2200) {
                boss.capturePhase = 'retreat';
                boss.beam = 0;
            }
        } else if (boss.capturePhase === 'retreat') {
            // Return to formation slot.
            const pos = slotPosition(boss);
            boss.x += (pos.x - boss.x) * 0.05 * dt;
            boss.y += (pos.y - boss.y) * 0.05 * dt;
            if (Math.abs(boss.y - pos.y) < 2 && Math.abs(boss.x - pos.x) < 2) {
                boss.isCapturing = false;
                // If it didn't capture anyone it returns to formation;
                // if it holds a fighter it stays in formation displaying it.
                boss.state = 'formation';
                captureActive = false;
                captureCooldown = 8000 + Math.random() * 5000;
            }
        }
    }

    // The tractor beam is a downward cone. Test if the player is inside it.
    function isPlayerInBeam(boss) {
        const beamTopY = boss.y + boss.h;
        const beamBottomY = VH;
        if (PLAYER_Y < beamTopY) return false;
        const span = (beamBottomY - beamTopY);
        const frac = (PLAYER_Y - beamTopY) / Math.max(1, span);
        const halfWidth = (4 + frac * 22) * boss.beam;
        return Math.abs(player.x - boss.x) < halfWidth;
    }

    function triggerCapture(boss) {
        player.captured = true;
        boss.holdsCaptured = true;
        capturedHolder = boss;
        // Lose a life for the capture (authentic-ish: ship is taken).
        loseLife(true);
        sfx.capture();
        // Visual: a captured ship will be drawn docked above the boss.
    }

    // ---- Enemy bullets --------------------------------------------------
    function fireEnemyBullet(e) {
        // Aimed slightly toward the player.
        const dx = player.x - e.x;
        const dy = PLAYER_Y - e.y;
        const d = Math.max(1, Math.hypot(dx, dy));
        enemyBullets.push({
            x: e.x, y: e.y + e.h / 2,
            vx: (dx / d) * ENEMY_BULLET_SPEED * 0.5,
            vy: ENEMY_BULLET_SPEED
        });
    }

    // ---- Player ---------------------------------------------------------
    // All gameplay timing uses performance.now (the audio clock is separate).
    function tnow() { return simulationTime; }

    function updatePlayer(dt) {
        if (!player.alive) return;
        const moveLeft = keys['ArrowLeft'] || keys['a'] || keys['A'];
        const moveRight = keys['ArrowRight'] || keys['d'] || keys['D'];
        const half = player.dual ? 13 : 6;
        if (moveLeft) player.x = Math.max(half + 1, player.x - PLAYER_SPEED * dt);
        if (moveRight) player.x = Math.min(VW - half - 1, player.x + PLAYER_SPEED * dt);

        if (keys[' '] || keys['Space']) {
            tryShoot();
        }
    }

    function tryShoot() {
        const t = tnow();
        if (t - lastShotTime < SHOT_COOLDOWN) return;
        const maxBullets = player.dual ? 4 : 2;
        if (bullets.length >= maxBullets) return;
        if (player.dual) {
            bullets.push({ x: player.x - 7, y: PLAYER_Y, vy: -PLAYER_BULLET_SPEED });
            bullets.push({ x: player.x + 7, y: PLAYER_Y, vy: -PLAYER_BULLET_SPEED });
        } else {
            bullets.push({ x: player.x, y: PLAYER_Y, vy: -PLAYER_BULLET_SPEED });
        }
        shotsFired += player.dual ? 2 : 1;
        lastShotTime = t;
        sfx.shoot();
    }

    function loseLife(fromCapture) {
        // Dual fighter? Lose the second ship instead of a life.
        if (player.dual && !fromCapture) {
            player.dual = false;
            player.invulnUntil = tnow() + RESPAWN_INVULN;
            spawnExplosion(player.x + 7, PLAYER_Y);
            sfx.explode();
            shake(220);
            return;
        }
        lives--;
        updateUI();
        spawnExplosion(player.x, PLAYER_Y);
        sfx.explode();
        shake(320);
        if (lives <= 0) {
            player.alive = false;
            gameState = 'dying';
            dyingTimer = 900;
        } else {
            // Respawn after brief delay with invulnerability.
            player.alive = false;
            player.respawning = true;
            gameState = 'dying';
            dyingTimer = 700;
        }
    }

    let dyingTimer = 0;
    function respawnPlayer() {
        player.alive = true;
        player.respawning = false;
        player.x = VW / 2;
        player.invulnUntil = tnow() + RESPAWN_INVULN;
        // Challenge stages run entirely in the 'flyin' state (that is where the
        // wave queue drains); returning to 'playing' there would soft-lock.
        gameState = isChallenge ? 'flyin' : 'playing';
    }

    // ---- Rescue / dual fighter ------------------------------------------
    function rescueCapturedFighter(boss) {
        // The boss holding the captured ship was destroyed: free the ship.
        capturedHolder = null;
        player.captured = false;
        // Animate the freed ship docking next to the player => dual fighter.
        player.dual = true;
        player.invulnUntil = tnow() + 1000;
        sfx.rescue();
        spawnPopup(player.x - 10, PLAYER_Y - 14, 'DUAL', '#28e0f0');
        // a few celebratory sparks
        for (let i = 0; i < 12; i++) spawnSpark(player.x, PLAYER_Y, '#28e0f0');
    }

    // ---- Bullets update -------------------------------------------------
    function updateBullets(dt) {
        for (let i = bullets.length - 1; i >= 0; i--) {
            bullets[i].y += bullets[i].vy * dt;
            if (bullets[i].y < -6) bullets.splice(i, 1);
        }
        for (let i = enemyBullets.length - 1; i >= 0; i--) {
            const b = enemyBullets[i];
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            if (b.y > VH + 6 || b.x < -6 || b.x > VW + 6) enemyBullets.splice(i, 1);
        }
    }

    // ---- Particles / popups --------------------------------------------
    function spawnExplosion(x, y) {
        particles.push({ type: 'exp', x: x - 8, y: y - 8, frame: 0, t: 0 });
    }
    function spawnSpark(x, y, color) {
        particles.push({
            type: 'spark', x: x, y: y,
            vx: (Math.random() - 0.5) * 3,
            vy: (Math.random() - 0.5) * 3,
            life: 1, color: color || '#ffe000', size: 1 + Math.random() * 2
        });
    }
    function updateParticles(dt) {
        for (let i = particles.length - 1; i >= 0; i--) {
            const p = particles[i];
            if (p.type === 'exp') {
                p.t += dt;
                if (p.t > 4) { p.t = 0; p.frame++; if (p.frame >= EXP.length) { particles.splice(i, 1); } }
            } else {
                p.x += p.vx * dt;
                p.y += p.vy * dt;
                p.life -= 0.04 * dt;
                p.size *= 0.96;
                if (p.life <= 0) particles.splice(i, 1);
            }
        }
    }
    function spawnPopup(x, y, text, color) {
        popups.push({ x: x, y: y, text: text, color: color || '#fff', life: 1 });
    }
    function updatePopups(dt) {
        for (let i = popups.length - 1; i >= 0; i--) {
            popups[i].y -= 0.3 * dt;
            popups[i].life -= 0.012 * dt;
            if (popups[i].life <= 0) popups.splice(i, 1);
        }
    }

    // ---- Collisions -----------------------------------------------------
    function checkCollisions() {
        // Player bullets vs enemies.
        for (let bi = bullets.length - 1; bi >= 0; bi--) {
            const b = bullets[bi];
            for (let ei = enemies.length - 1; ei >= 0; ei--) {
                const e = enemies[ei];
                const hw = e.w / 2, hh = e.h / 2;
                if (b.x > e.x - hw && b.x < e.x + hw &&
                    b.y > e.y - hh && b.y < e.y + hh) {
                    bullets.splice(bi, 1);
                    shotsHit++;
                    hitEnemy(e, ei);
                    break;
                }
            }
        }

        // Enemy bullets vs player.
        if (player.alive && tnow() > player.invulnUntil) {
            for (let i = enemyBullets.length - 1; i >= 0; i--) {
                const b = enemyBullets[i];
                if (playerHitTest(b.x, b.y, 1)) {
                    enemyBullets.splice(i, 1);
                    loseLife(false);
                    break;
                }
            }
        }

        // Diving enemies vs player (collision).
        if (player.alive && tnow() > player.invulnUntil) {
            for (let i = enemies.length - 1; i >= 0; i--) {
                const e = enemies[i];
                if (e.state !== 'dive' && e.state !== 'capture' && !e.challenge) continue;
                if (e.state === 'capture') continue; // beam handled separately
                if (playerHitTest(e.x, e.y, e.w / 2)) {
                    // Destroy the enemy too.
                    spawnExplosion(e.x, e.y);
                    enemies.splice(i, 1);
                    loseLife(false);
                    break;
                }
            }
        }
    }

    // Test a point/radius against the player's hitbox (handles dual width).
    function playerHitTest(x, y, r) {
        const half = player.dual ? 13 : 6;
        return x > player.x - half - r && x < player.x + half + r &&
               y > PLAYER_Y - 7 - r && y < PLAYER_Y + 7 + r;
    }

    function hitEnemy(e, ei) {
        sfx.hit();
        if (e.kind === 'boss') {
            e.hp--;
            if (e.hp > 0) {
                // First hit: flip to damaged blue/purple flashing state.
                e.hurtFlash = 240;
                for (let i = 0; i < 4; i++) spawnSpark(e.x, e.y, '#c040ff');
                return;
            }
        }
        // Destroyed.
        destroyEnemy(e, ei);
    }

    function destroyEnemy(e, ei) {
        // Scoring: diving worth more; bosses escalate with escort count.
        let pts;
        const diving = (e.state === 'dive' || e.state === 'capture' || e.challenge);
        if (e.kind === 'boss') {
            if (!diving) {
                pts = 150;
            } else {
                // Count escorts currently diving with this boss.
                let escorts = 0;
                for (let k = 0; k < enemies.length; k++) {
                    if (enemies[k].escortLeader === e && enemies[k].state === 'dive') escorts++;
                }
                pts = escorts >= 2 ? 1600 : (escorts === 1 ? 800 : 400);
            }
        } else {
            pts = diving ? KIND[e.kind].scoreDive : KIND[e.kind].scoreForm;
        }
        score += pts;
        if (e.challenge) challengeKills++;
        spawnPopup(e.x - 6, e.y, String(pts), e.kind === 'boss' ? '#28f0a0' : '#ffe000');
        spawnExplosion(e.x, e.y);
        sfx.explode();

        // If this boss held the captured fighter, free it (dual fighter!).
        if (e.holdsCaptured && capturedHolder === e) {
            rescueCapturedFighter(e);
        }
        if (e === capturedHolder) capturedHolder = null;
        if (e.isCapturing) { captureActive = false; captureCooldown = 6000; }

        enemies.splice(ei, 1);
        updateUI();

        // Stage clear?
        if (!isChallenge && enemies.length === 0 && flyInQueue.length === 0) {
            stageClear();
        }
    }

    function stageClear() {
        gameState = 'stage-clear';
        stageClearTimer = 1500;
        sfx.stageStart();
    }

    // ---- UI -------------------------------------------------------------
    function updateUI() {
        if (score >= nextExtraLife) {
            lives++;
            nextExtraLife += 50000;
            spawnPopup(player.x - 12, PLAYER_Y - 24, '1UP', '#28f0a0');
            sfx.rescue();
        }
        const accuracy = shotsFired ? Math.round(shotsHit / shotsFired * 100) : 0;
        arcade.status(`Stage ${stage} · Best ${highScore.toLocaleString()} · Accuracy ${accuracy}% · Next life ${nextExtraLife.toLocaleString()}`, score / nextExtraLife * 100);
        document.getElementById('score').textContent = score;
        document.getElementById('lives').textContent = Math.max(0, lives);
        document.getElementById('wave').textContent = stage;
        if (score > highScore) {
            highScore = score;
            try { localStorage.setItem('galagaHighScore', String(highScore)); } catch (e) {}
        }
    }

    // ---- Screen shake ---------------------------------------------------
    function shake(ms) {
        if (reduceMotion) return;
        shakeTime = Math.max(shakeTime, ms);
    }

    // ---- Drawing --------------------------------------------------------
    function drawPlayer() {
        if (!player.alive) return;
        // Blink while invulnerable.
        const invuln = tnow() < player.invulnUntil;
        if (invuln && Math.floor(tnow() / 80) % 2 === 0) return;

        if (player.dual) {
            drawSprite(SPR_PLAYER, PAL, player.x - 6 - 7, PLAYER_Y - 8, 1);
            drawSprite(SPR_PLAYER, PAL, player.x - 6 + 7, PLAYER_Y - 8, 1);
        } else {
            drawSprite(SPR_PLAYER, PAL, player.x - 6, PLAYER_Y - 8, 1);
        }
    }

    function drawEnemy(e) {
        const meta = KIND[e.kind];
        let grid, pal;
        const frame = (Math.floor(e.flap) % 2 === 0) ? 0 : 1;
        if (e.kind === 'boss') {
            pal = PAL;
            if (e.hp <= 1 && meta.hp === 2) {
                // Damaged: flash between hurt sprite and frames.
                grid = (Math.floor(tnow() / 120) % 2 === 0) ? SPR_BOSS_HURT : meta.frames[frame];
            } else if (e.hurtFlash > 0) {
                grid = SPR_BOSS_HURT;
            } else {
                grid = meta.frames[frame];
            }
        } else {
            pal = PAL;
            grid = meta.frames[frame];
        }

        // Rotate diving enemies slightly to face travel direction. A boss that
        // holds a captured fighter keeps it docked above itself in every state
        // (formation AND dive), so it never blinks out mid-swoop.
        if ((e.state === 'dive' || e.state === 'flyin') && Math.abs(e.angle) > 0.05) {
            ctx.save();
            ctx.translate(Math.round(e.x), Math.round(e.y));
            ctx.rotate(e.angle);
            drawSprite(grid, pal, -e.w / 2, -e.h / 2, 1);
            if (e.holdsCaptured) {
                drawSprite(SPR_PLAYER, PAL_CAPTURED, -6, -e.h / 2 - 17, 1);
            }
            ctx.restore();
        } else {
            drawSprite(grid, pal, e.x - e.w / 2, e.y - e.h / 2, 1);
            if (e.holdsCaptured) {
                drawSprite(SPR_PLAYER, PAL_CAPTURED, e.x - 6, e.y - e.h / 2 - 17, 1);
            }
        }
    }

    function drawTractorBeam(e) {
        if (e.state !== 'capture' || e.beam <= 0) return;
        const topY = e.y + e.h / 2;
        const segments = 5;
        const maxHalf = 22 * e.beam;
        // Pulsing brightness.
        const pulse = 0.4 + 0.4 * Math.sin(tnow() / 80);
        for (let s = 0; s < segments; s++) {
            const f0 = s / segments;
            const f1 = (s + 1) / segments;
            const y0 = topY + f0 * (VH - topY);
            const y1 = topY + f1 * (VH - topY);
            const hw0 = 4 + f0 * (maxHalf - 4);
            const hw1 = 4 + f1 * (maxHalf - 4);
            // Alternating bands for the classic striped look.
            const bright = (s % 2 === 0) ? pulse : pulse * 0.5;
            ctx.fillStyle = 'rgba(120,180,255,' + (0.25 + bright * 0.4) + ')';
            ctx.beginPath();
            ctx.moveTo(e.x - hw0, y0);
            ctx.lineTo(e.x + hw0, y0);
            ctx.lineTo(e.x + hw1, y1);
            ctx.lineTo(e.x - hw1, y1);
            ctx.closePath();
            ctx.fill();
        }
    }

    function drawBullets() {
        ctx.fillStyle = '#ffffff';
        for (let i = 0; i < bullets.length; i++) {
            const b = bullets[i];
            ctx.fillRect(Math.round(b.x), Math.round(b.y) - 3, 1, 4);
            ctx.fillStyle = '#28e0f0';
            ctx.fillRect(Math.round(b.x), Math.round(b.y) - 1, 1, 2);
            ctx.fillStyle = '#ffffff';
        }
        // Enemy bullets: small yellow/orange darts.
        for (let i = 0; i < enemyBullets.length; i++) {
            const b = enemyBullets[i];
            ctx.fillStyle = '#ffe000';
            ctx.fillRect(Math.round(b.x) - 1, Math.round(b.y), 2, 3);
            ctx.fillStyle = '#ff8000';
            ctx.fillRect(Math.round(b.x), Math.round(b.y) + 1, 1, 1);
        }
    }

    function drawParticles() {
        for (let i = 0; i < particles.length; i++) {
            const p = particles[i];
            if (p.type === 'exp') {
                drawSprite(EXP[p.frame], PAL_EXP, p.x, p.y, 1);
            } else {
                ctx.globalAlpha = Math.max(0, p.life);
                ctx.fillStyle = p.color;
                ctx.fillRect(Math.round(p.x), Math.round(p.y), Math.ceil(p.size), Math.ceil(p.size));
                ctx.globalAlpha = 1;
            }
        }
    }

    function drawPopups() {
        for (let i = 0; i < popups.length; i++) {
            const p = popups[i];
            ctx.globalAlpha = Math.max(0, Math.min(1, p.life * 1.5));
            drawText(p.text, Math.round(p.x), Math.round(p.y), 1, p.color);
            ctx.globalAlpha = 1;
        }
    }

    // ---- Stage flags (number-flag badges) ------------------------------
    // Flag denominations and colors, classic Galaga bottom-right markers.
    const FLAG_DEFS = [
        { v: 50, col: '#28d028', txt: '50' },
        { v: 30, col: '#ff2020', txt: '30' },
        { v: 20, col: '#3858ff', txt: '20' },
        { v: 10, col: '#28e0f0', txt: '10' },
        { v: 5,  col: '#ffe000', txt: '5'  },
        { v: 1,  col: '#ffffff', txt: '1'  }
    ];
    function drawStageFlags() {
        let remaining = stage;
        const flags = [];
        for (let i = 0; i < FLAG_DEFS.length; i++) {
            while (remaining >= FLAG_DEFS[i].v) {
                flags.push(FLAG_DEFS[i]);
                remaining -= FLAG_DEFS[i].v;
                if (flags.length > 12) break; // safety cap
            }
            if (flags.length > 12) break;
        }
        // Draw from bottom-right going left.
        let fx = VW - 2;
        const fy = VH - 9;
        for (let i = 0; i < flags.length; i++) {
            const f = flags[i];
            fx -= 9;
            // little flag body
            ctx.fillStyle = f.col;
            ctx.fillRect(fx, fy, 8, 7);
            ctx.fillStyle = '#000';
            // number in 3px-ish, draw with small font scaled to fit
            drawText(f.txt, fx + 1, fy + 1, 1, '#000');
        }
    }

    // ---- Lives icons ----------------------------------------------------
    function drawLivesIcons() {
        const n = Math.max(0, lives - (gameState === 'dying' ? 0 : 0));
        for (let i = 0; i < Math.min(n, 6); i++) {
            drawSprite(SPR_PLAYER, PAL, 2 + i * 12, VH - 17, 1);
        }
    }

    // ---- HUD (in-canvas arcade text) -----------------------------------
    let oneUpBlink = 0;
    function drawHUD() {
        oneUpBlink += 1;
        // "1UP" blinks; score beneath it.
        if (Math.floor(oneUpBlink / 30) % 2 === 0) {
            drawText('1UP', 12, 2, 1, '#ff2020');
        }
        const sc = String(score);
        drawText(sc, 12, 10, 1, '#ffffff');

        drawTextCentered('HIGH SCORE', 2, 1, '#ff2020');
        const hs = String(highScore);
        drawText(hs, Math.round((VW - textWidth(hs, 1)) / 2), 10, 1, '#ffffff');

        // Stage number top-right.
        drawText('ST', VW - 30, 2, 1, '#28e0f0');
        drawText(String(stage), VW - 14, 2, 1, '#ffe000');
    }

    // ---- Main draw ------------------------------------------------------
    function draw() {
        // Apply scale transform so all logical coords render at 2x.
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Screen shake offset (disabled under reduced motion).
        let ox = 0, oy = 0;
        if (shakeTime > 0 && !reduceMotion) {
            ox = (Math.random() - 0.5) * 4;
            oy = (Math.random() - 0.5) * 4;
        }
        ctx.setTransform(SCALE, 0, 0, SCALE, ox * SCALE, oy * SCALE);

        drawStars();

        // World objects.
        for (let i = 0; i < enemies.length; i++) {
            if (enemies[i].state === 'capture') drawTractorBeam(enemies[i]);
        }
        for (let i = 0; i < enemies.length; i++) drawEnemy(enemies[i]);
        drawBullets();
        drawParticles();
        drawPlayer();
        drawPopups();

        // HUD overlays.
        drawHUD();
        drawLivesIcons();
        drawStageFlags();

        // Banners.
        if (stageBannerTimer > 0) {
            if (isChallenge) {
                drawTextCentered('CHALLENGING', VH / 2 - 14, 1, '#28e0f0');
                drawTextCentered('STAGE', VH / 2 - 4, 1, '#28e0f0');
            } else {
                drawTextCentered('STAGE ' + stage, VH / 2 - 6, 1, '#28e0f0');
            }
        }
        if (perfectTimer > 0) {
            drawTextCentered('PERFECT !', VH / 2 - 6, 2, '#ffe000');
            drawTextCentered('10000 PTS', VH / 2 + 8, 1, '#ffffff');
        }
        if (gameState === 'gameover') {
            drawTextCentered('GAME OVER', VH / 2 - 6, 1, '#ff2020');
        }
    }

    // ---- Main loop ------------------------------------------------------
    let lastTime = performance.now();
    let accumulator = 0;
    function simulate() {
        const dtMs = 1000 / 60;
        const dt = 1;
        simulationTime += dtMs;

        // Timers.
        if (shakeTime > 0) shakeTime -= dtMs;
        if (stageBannerTimer > 0) stageBannerTimer -= dtMs;
        if (perfectTimer > 0) perfectTimer -= dtMs;
        if (captureCooldown > 0) captureCooldown -= dtMs;

        updateStars(dt);
        updateParticles(dt);
        updatePopups(dt);

        if (gameState === 'flyin') {
            updatePlayer(dt);
            updateEnemies(dt, dtMs);
            updateBullets(dt);
            checkCollisions();
            updateFlyIn(dt, dtMs);
        } else if (gameState === 'playing') {
            updatePlayer(dt);
            updateEnemies(dt, dtMs);
            updateBullets(dt);
            checkCollisions();

            // Schedule dives.
            diveTimer += dtMs;
            const diveInterval = Math.max(700, 2200 - stage * 120);
            if (diveTimer > diveInterval && !isChallenge) {
                diveTimer = 0;
                pickDivers();
            }
        } else if (gameState === 'dying') {
            updateEnemies(dt, dtMs);
            updateBullets(dt);
            dyingTimer -= dtMs;
            if (dyingTimer <= 0) {
                if (lives <= 0) {
                    doGameOver();
                } else {
                    respawnPlayer();
                }
            }
        } else if (gameState === 'stage-clear') {
            updateEnemies(dt, dtMs);
            stageClearTimer -= dtMs;
            if (stageClearTimer <= 0) {
                stage++;
                beginStage();
            }
        }

    }
    function loop(t) {
        const elapsed = Math.min(80, t - lastTime);
        lastTime = t;
        if (!arcade.paused) {
            accumulator += elapsed;
            while (accumulator >= 1000 / 60) {
                simulate();
                accumulator -= 1000 / 60;
            }
            draw();
            updateUI();
        } else accumulator = 0;
        requestAnimationFrame(loop);
    }

    // ---- Game over ------------------------------------------------------
    function doGameOver() {
        gameState = 'gameover';
        updateUI();
        document.getElementById('finalScore').textContent = score;
        document.getElementById('run-stats').textContent = `Stage ${stage} · ${shotsFired} shots · ${shotsFired ? Math.round(shotsHit / shotsFired * 100) : 0}% accuracy`;
        document.getElementById('gameOverScreen').classList.remove('hidden');
        updateMobileControlsVisibility();
    }

    // ---- Start / restart ------------------------------------------------
    function startGame() {
        resumeAudio();
        score = 0;
        shotsFired = shotsHit = 0;
        nextExtraLife = 20000;
        lastShotTime = -SHOT_COOLDOWN;
        lives = 3;
        stage = 1;
        bullets = [];
        enemyBullets = [];
        particles = [];
        popups = [];
        player.x = VW / 2;
        player.alive = true;
        player.dual = false;
        player.captured = false;
        player.invulnUntil = 0;
        capturedHolder = null;
        captureActive = false;
        document.getElementById('startScreen').classList.add('hidden');
        document.getElementById('gameOverScreen').classList.add('hidden');
        updateMobileControlsVisibility();
        beginStage();
    }

    // ---- Mobile controls visibility ------------------------------------
    function updateMobileControlsVisibility() {
        const mobileControls = document.getElementById('mobileControls');
        const startScreen = document.getElementById('startScreen');
        const gameOverScreen = document.getElementById('gameOverScreen');
        const dialogVisible = !startScreen.classList.contains('hidden') ||
                              !gameOverScreen.classList.contains('hidden');
        if (dialogVisible) mobileControls.classList.add('hidden');
        else mobileControls.classList.remove('hidden');
    }

    // ---- Input ----------------------------------------------------------
    document.addEventListener('keydown', function (e) {
        keys[e.key] = true;
        if (e.key === ' ' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
            e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
        }
    });
    document.addEventListener('keyup', function (e) {
        keys[e.key] = false;
    });

    document.getElementById('startBtn').addEventListener('click', startGame);
    document.getElementById('restartBtn').addEventListener('click', startGame);

    // Touch buttons drive the same `keys` object as the keyboard.
    function setupTouchButton(button, keyName) {
        const activate = function (e) {
            e.preventDefault();
            keys[keyName] = true;
            button.classList.add('active');
            resumeAudio();
        };
        const deactivate = function (e) {
            e.preventDefault();
            keys[keyName] = false;
            button.classList.remove('active');
        };
        bindPointer(button, () => activate({ preventDefault() {} }), () => deactivate({ preventDefault() {} }));
    }
    setupTouchButton(document.getElementById('btnLeft'), 'ArrowLeft');
    setupTouchButton(document.getElementById('btnRight'), 'ArrowRight');
    setupTouchButton(document.getElementById('btnFire'), ' ');

    // Prevent page scroll while playing on touch.
    document.getElementById('gameContainer').addEventListener('touchmove', function (e) {
        if (gameState !== 'start' && gameState !== 'gameover') e.preventDefault();
    }, { passive: false });

    // Resume audio on first canvas touch.
    canvas.addEventListener('touchstart', function () { resumeAudio(); }, { passive: true });

    // ---- Boot -----------------------------------------------------------
    arcade.mount({
      title: 'Galaga',
      help: 'Move with ← → or A/D. Hold Space to fire. Destroy a diving boss holding your fighter to recover it and fly dual. Earn an extra life at 20,000, then every 50,000 points. P pauses; M toggles sound.',
      active: () => !['start', 'gameover'].includes(gameState),
      clearInput: () => Object.keys(keys).forEach(key => { keys[key] = false; }),
      restart: startGame,
      onResume: () => { lastTime = performance.now(); accumulator = 0; },
    });
    initStars();
    updateUI();
    updateMobileControlsVisibility();
    requestAnimationFrame(loop);
